/**
 * What the service worker shows for a push: the bottle alert the server sent
 * (server/services/push-rules.ts `pushData`), checked field by field. A push
 * the worker can't read still shows a notification, because Safari revokes a
 * subscription whose pushes show nothing. Pure, so the app's tests run it.
 */
import { STANDALONE_PAGES } from "../../shared/public-pages.ts";

/** The fields the worker reads from a push (the server's PushData, version 1). */
export interface Alert {
  title: string;
  body: string;
  /** Per baby: a newer alert replaces the older one in the tray. */
  tag: string;
  /** Where a tap opens: always a path on this site. */
  url: string;
  babyId: string | null;
  check: BottleCheck | null;
}

/** The checks that send a push (server/services/push-rules.ts BottleCheck). */
export type BottleCheck = "overdue" | "twin";

/** A check name from a push, a message, or a URL, or null for anything else. */
export function bottleCheck(value: unknown): BottleCheck | null {
  return value === "overdue" || value === "twin" ? value : null;
}

/** Shown for a push that isn't a readable bottle alert. */
export const FALLBACK_ALERT: Alert = {
  title: "Babia",
  body: "A bottle may not be logged yet. Open Babia to check.",
  tag: "babia",
  url: "/",
  babyId: null,
  check: null,
};

const text = (value: unknown, max: number): string | null =>
  typeof value === "string" && value.trim() ? value.slice(0, max) : null;

const SAME_SITE = "https://babia.invalid";

/**
 * A path on this site, or "/": a push never sends a tap to another origin.
 * The URL parser decides, because it reads `/\host` and `/\t/host` as
 * another host, which a check of the first characters would let through.
 */
export function safePath(url: unknown): string {
  if (typeof url !== "string" || !url.startsWith("/")) return "/";
  try {
    const parsed = new URL(url, SAME_SITE);
    return parsed.origin === SAME_SITE ? parsed.pathname + parsed.search + parsed.hash : "/";
  } catch {
    return "/";
  }
}

/**
 * Pages that open outside the app shell (components/layout.tsx RootLayout):
 * the legal, support, and comparison pages, sign-in, and an assistant's consent, which
 * never handle a tapped alert. A tap there opens Babia in a new window
 * instead, so an assistant's sign-in isn't navigated away.
 */
const OUTSIDE_APP = new Set<string>([...STANDALONE_PAGES, "/login", "/consent"]);

/** Whether a window at `url` shows the app, which can open a tapped alert's baby. */
export function isAppPage(url: string): boolean {
  try {
    return !OUTSIDE_APP.has(new URL(url).pathname.replace(/\/+$/, ""));
  } catch {
    return false;
  }
}

/** The alert in a push's text, or the fallback for anything else. */
export function readPush(raw: string | null): Alert {
  if (!raw) return FALLBACK_ALERT;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return FALLBACK_ALERT;
  }
  if (typeof data !== "object" || data === null || (data as { v?: unknown }).v !== 1)
    return FALLBACK_ALERT;
  const d = data as Record<string, unknown>;
  const title = text(d.title, 120);
  if (!title) return FALLBACK_ALERT;
  return {
    title,
    body: text(d.body, 240) ?? "",
    tag: text(d.tag, 64) ?? FALLBACK_ALERT.tag,
    url: safePath(d.url),
    babyId: text(d.babyId, 64),
    check: bottleCheck(d.check),
  };
}

/**
 * The message a tapped notification posts to an open Babia window
 * (lib/push-clicks.ts). The window answers on the message's port once it has
 * opened the baby; without an answer the worker loads the alert's page there.
 */
export interface OpenBabyMessage {
  type: "babia:open-baby";
  babyId: string | null;
  check: BottleCheck | null;
}

export const OPEN_BABY = "babia:open-baby";

/**
 * How long the worker waits for a window's answer before loading the page
 * itself. An open Babia answers at once. Firefox lets a worker open a window
 * only in the first second after the tap, so the fallback must start before that.
 */
export const OPEN_BABY_ACK_MS = 500;
